import type { ExerciseConfig, Category } from './types';
import { CATEGORY_ORDER } from './categories';

/**
 * Every exercise the app has ever offered, including retired ones.
 *
 * Retiring an exercise means `isActive: false`, never deleting the entry. The
 * pickers all filter on `isActive`, so a retired lift disappears from the UI -
 * but workouts already logged against it are still read back, and both the
 * history list and the progress chart look its display name up by id here. Delete
 * the entry and those fall back to the name snapshot stored on each workout,
 * losing the multiplier and letting a renamed lift drift out of sync.
 *
 * Retiring never changes anyone's score: `normalizationMultiplier` is snapshotted
 * onto each workout when it is logged.
 */
export const DEFAULT_EXERCISES: ExerciseConfig[] = [
  {
    id: 'barbell_bench_press',
    category: 'push',
    displayName: 'Barbell Bench Press',
    normalizationMultiplier: 1.0,
    inputWeightMultiplier: 1,
    isCompetitionLift: true,
    isActive: true,
    sortOrder: 1,
  },
  {
    id: 'incline_barbell_bench_press',
    category: 'push',
    displayName: 'Incline Barbell Bench Press',
    normalizationMultiplier: 1.1,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    isActive: true,
    sortOrder: 2,
  },
  {
    id: 'flat_dumbbell_bench_press',
    category: 'push',
    displayName: 'Flat Dumbbell Bench Press',
    normalizationMultiplier: 1.2,
    inputWeightMultiplier: 2,
    isCompetitionLift: false,
    isActive: true,
    sortOrder: 3,
  },
  {
    id: 'close_grip_barbell_bench_press',
    category: 'push',
    // Most lifters press roughly 90-95% of their competition bench close-grip:
    // shorter range of motion, but the triceps carry it without the chest's
    // leverage. 1.08 credits it a touch above flat and below incline.
    displayName: 'Close-Grip Barbell Bench Press',
    normalizationMultiplier: 1.08,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    isActive: true,
    sortOrder: 4,
  },
  {
    id: 'overhead_press',
    category: 'push',
    displayName: 'Overhead Press',
    normalizationMultiplier: 1.5,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    isActive: true,
    sortOrder: 5,
  },
  {
    id: 'barbell_bent_over_row',
    category: 'row',
    displayName: 'Barbell Bent-Over Row',
    normalizationMultiplier: 1.0,
    inputWeightMultiplier: 1,
    isCompetitionLift: true,
    isActive: true,
    sortOrder: 1,
  },
  {
    id: 'pull_up',
    category: 'row',
    displayName: 'Pull-up',
    // Full body weight is the resistance; scored against the pull-up benchmark
    // tables in pullupBenchmarks.ts rather than via DOTS.
    bodyWeightFactor: 1.0,
    normalizationMultiplier: 1.0,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    isActive: true,
    sortOrder: 2,
  },
  {
    id: 'chin_up',
    category: 'row',
    displayName: 'Chin-up',
    bodyWeightFactor: 1.0,
    // Slightly easier than a pull-up, so the same set is worth a little less.
    normalizationMultiplier: 0.95,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    isActive: true,
    sortOrder: 3,
  },
  {
    id: 'lat_pulldown',
    category: 'row',
    displayName: 'Lat Pulldown',
    // Machine-supported, so a given weight is easier than a strict row.
    normalizationMultiplier: 0.9,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    isActive: true,
    sortOrder: 4,
  },
  {
    id: 'single_arm_dumbbell_row',
    category: 'row',
    displayName: 'Single-Arm Dumbbell Row',
    // One arm at a time, so the entered weight is NOT doubled - unlike the flat
    // dumbbell press, where both hands work at once. A 100 lb single-arm row is
    // credited as a 175 lb barbell row.
    normalizationMultiplier: 1.75,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    isActive: true,
    sortOrder: 5,
  },
  {
    id: 'chest_supported_row',
    category: 'row',
    displayName: 'Chest-Supported Row',
    // Strictest row: no body english to help.
    normalizationMultiplier: 1.1,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    // RETIRED - hidden from the pickers. The config stays so previously logged
    // chest-supported rows keep their name and multiplier when read back; see the
    // note above DEFAULT_EXERCISES.
    isActive: false,
    sortOrder: 6,
  },
  {
    id: 'conventional_deadlift',
    category: 'hinge',
    displayName: 'Conventional Deadlift',
    normalizationMultiplier: 1.0,
    inputWeightMultiplier: 1,
    isCompetitionLift: true,
    isActive: true,
    sortOrder: 1,
  },
  {
    id: 'romanian_deadlift',
    category: 'hinge',
    displayName: 'Romanian Deadlift',
    // 1.5 credited an RDL almost as if the shortened range and lighter loads
    // were a full pull. 1.2 is closer to what an RDL actually converts to.
    normalizationMultiplier: 1.2,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    isActive: true,
    sortOrder: 2,
  },
  {
    id: 'trap_bar_deadlift',
    category: 'hinge',
    displayName: 'Trap Bar Deadlift',
    normalizationMultiplier: 0.95,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    isActive: true,
    sortOrder: 3,
  },
  {
    id: 'barbell_back_squat',
    category: 'squat',
    displayName: 'Barbell Back Squat',
    normalizationMultiplier: 1.0,
    inputWeightMultiplier: 1,
    isCompetitionLift: true,
    isActive: true,
    sortOrder: 1,
  },
  {
    id: 'barbell_front_squat',
    category: 'squat',
    displayName: 'Barbell Front Squat',
    normalizationMultiplier: 1.15,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    isActive: true,
    sortOrder: 2,
  },
  {
    id: 'safety_bar_squat',
    category: 'squat',
    displayName: 'Safety Bar Squat',
    normalizationMultiplier: 1.05,
    inputWeightMultiplier: 1,
    isCompetitionLift: false,
    // RETIRED - hidden from the pickers. The config stays so previously logged
    // safety bar squats keep their name and multiplier when read back; see the
    // note above DEFAULT_EXERCISES.
    isActive: false,
    sortOrder: 3,
  },
];

/**
 * The multipliers as shipped, captured before anything can overwrite them.
 *
 * The admin panel can retune `normalizationMultiplier` at runtime; clearing an
 * override has to restore the shipped number, so it is remembered here rather
 * than lost the moment an override is applied.
 */
const SHIPPED_MULTIPLIERS: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(DEFAULT_EXERCISES.map((ex) => [ex.id, ex.normalizationMultiplier])),
);

/** The multiplier this exercise ships with, ignoring any admin override. */
export function shippedMultiplier(exerciseId: string): number | undefined {
  return SHIPPED_MULTIPLIERS[exerciseId];
}

/**
 * Applies admin-set normalization multipliers over the shipped catalog.
 *
 * This deliberately writes into the catalog entries in place rather than
 * returning a new array. Several call sites hold on to `DEFAULT_EXERCISES` or to
 * individual configs, and an override that only some of them could see would
 * score a lift differently depending on which screen logged it. One mutable
 * catalog means there is exactly one answer.
 *
 * Only multipliers are overridable. Ids, categories, input multipliers and
 * active flags stay in code, where they are reviewed and tested.
 *
 * Existing workouts are untouched: each one snapshots the multiplier it was
 * scored with, so a change here applies to lifts logged from now on.
 *
 * @param overrides Exercise id to multiplier. Ids not present are reset to the
 *   shipped value, so this is the complete override state, not a patch.
 */
export function applyMultiplierOverrides(overrides: Record<string, number>): void {
  for (const ex of DEFAULT_EXERCISES) {
    const override = overrides[ex.id];
    ex.normalizationMultiplier =
      typeof override === 'number' && override > 0 ? override : SHIPPED_MULTIPLIERS[ex.id];
  }
}

/**
 * Gets all exercises for a specific category.
 *
 * @param category The category to filter by.
 * @returns Array of exercise configurations for the category.
 */
export function getExercisesByCategory(category: Category): ExerciseConfig[] {
  return DEFAULT_EXERCISES.filter((ex) => ex.category === category);
}

/**
 * Gets the competition lift for a specific category.
 *
 * @param category The category to find the competition lift for.
 * @returns The competition exercise configuration, or undefined if not found.
 */
export function getCompetitionLift(category: Category): ExerciseConfig | undefined {
  return DEFAULT_EXERCISES.find((ex) => ex.category === category && ex.isCompetitionLift);
}

/**
 * Gets all active exercises in the order categories are displayed everywhere
 * else - Push, Pull, Hinge, Squat - then by each category's sort order.
 *
 * Note this is CATEGORY_ORDER, not alphabetical: sorting the raw ids by name
 * would put Hinge first and Push third, which matches nothing else in the app.
 *
 * @returns Array of active exercise configurations.
 */
export function getActiveExercises(): ExerciseConfig[] {
  return DEFAULT_EXERCISES.filter((ex) => ex.isActive).sort((a, b) => {
    const byCategory = CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
    if (byCategory !== 0) return byCategory;
    return a.sortOrder - b.sortOrder;
  });
}
